import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import AgoraRTC, {
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
  useLocalMicrophoneTrack,
  useLocalCameraTrack,
  usePublish,
  useRemoteUsers,
} from "agora-rtc-react";
import { usePrevious } from "@web-src/utils/helper";
import isEqual from "lodash/isEqual";
import { useSelector } from "react-redux";
import { selectUserId } from "@web-src/modules/auth/store/authSlice";
import VirtualBackgroundExtension, {
  IVirtualBackgroundProcessor,
} from "agora-extension-virtual-background";
import {
  AIDenoiserExtension,
  AIDenoiserProcessorLevel,
  AIDenoiserProcessorMode,
} from "agora-extension-ai-denoiser";
import { useToast, useTranslations } from "@jugl-web/utils";
import { ActiveCallProps } from "../types";
import { useCallsSettings } from "./useCallsSettings";

const virtualBackgroundExtension = new VirtualBackgroundExtension();
const denoiser = new AIDenoiserExtension({
  assetsPath: "",
});

AgoraRTC.registerExtensions([virtualBackgroundExtension, denoiser]);

interface AgoraRTCRemoteUser extends IAgoraRTCRemoteUser {
  _uintid?: number;
}

type ActiveCallParams = {
  localAudioTrack: IMicrophoneAudioTrack | null;
  localVideoTrack: ICameraVideoTrack | null;
  remoteUsers: AgoraRTCRemoteUser[] | undefined;
  toggleVideo: (shouldSaveSetting?: boolean) => void;
  toggleAudio: (shouldSaveSetting?: boolean) => void;
  toggleProcessor: () => void;
  leaveCall: () => void;
  videoEnabled: boolean;
  audioEnabled: boolean;
  videoProcessor: IVirtualBackgroundProcessor | undefined;
};

export const useActiveCall = (
  client: IAgoraRTCClient,
  rtcProps: ActiveCallProps | undefined
): ActiveCallParams | null => {
  const { getCallsSettings, setCallsSettings } = useCallsSettings();
  const storedDevicesSettings = getCallsSettings();
  const videoProcessor = useRef<IVirtualBackgroundProcessor>(undefined);
  const [videoEnabled, setVideoEnabled] = useState<boolean>(
    storedDevicesSettings.isVideoOn || false
  );
  const [audioEnabled, setAudioEnabled] = useState<boolean>(
    storedDevicesSettings.isAudioOn || false
  );

  const { localMicrophoneTrack: localAudioTrack, error: localAudioTrackError } =
    useLocalMicrophoneTrack(audioEnabled, {
      encoderConfig: "speech_standard",
      ANS: true,
      AEC: true,
      microphoneId: storedDevicesSettings?.microphone_id,
    });

  const { localCameraTrack: localVideoTrack, error: localVideoTrackError } =
    useLocalCameraTrack(videoEnabled, {
      optimizationMode: "motion",
      encoderConfig: storedDevicesSettings.camera_resolution || "480p_1",
      cameraId: storedDevicesSettings?.camera_id || undefined,
    });

  usePublish([localAudioTrack, localVideoTrack]);

  const remoteUsers = useRemoteUsers(client);

  const meId = useSelector(selectUserId);
  const previousRtcProps = usePrevious(rtcProps);

  const [processorEnabled, setProcessorEnabled] = useState<boolean>();
  const { toast } = useToast({ variant: "web" });
  const { t } = useTranslations();

  const toggleVideo = useCallback(
    async (shouldSaveSettings = true) => {
      if (!localVideoTrack) {
        setVideoEnabled(true);
      }
      const nextIsDisabled = !localVideoTrack?.enabled;
      if (nextIsDisabled) {
        await client.disableDualStream();
      }
      await localVideoTrack?.setEnabled(nextIsDisabled);
      if (shouldSaveSettings) {
        setCallsSettings({
          isVideoOn: nextIsDisabled,
        });
      }
      setVideoEnabled(nextIsDisabled);
    },
    [localVideoTrack, setCallsSettings, client]
  );

  useEffect(() => {
    if (
      !rtcProps ||
      (previousRtcProps && isEqual(rtcProps, previousRtcProps))
    ) {
      return;
    }
    client
      .join(rtcProps.appId, rtcProps.channel, rtcProps.token, `web_${meId}`)
      .then(async () => {
        try {
          setAudioEnabled(true);
          setVideoEnabled(false);
          if (rtcProps.video) {
            toggleVideo();
            setVideoEnabled(true);
          } else {
            setVideoEnabled(false);
          }
        } catch (error) {
          if (error instanceof Error) {
            // do nothing
          }
        }
      });
  }, [
    rtcProps,
    previousRtcProps,
    client,
    meId,
    toggleVideo,
    getCallsSettings,
    toast,
  ]);

  useEffect(() => {
    const videoTrack = localVideoTrack as ICameraVideoTrack & {
      processor: unknown;
    };
    if (videoEnabled && localVideoTrack && !videoTrack.processor) {
      // oxlint-disable-next-line typescript/ban-ts-comment
      // @ts-ignore
      videoProcessor.current = virtualBackgroundExtension.createProcessor();
      videoProcessor.current?.init("../assets/agora-wasm.wasm");
      // oxlint-disable-next-line typescript/ban-ts-comment
      // @ts-ignore
      videoProcessor.current?.enable();
      localVideoTrack
        ?.pipe(videoProcessor.current)
        .pipe(localVideoTrack.processorDestination);
    }
  }, [localVideoTrack, videoEnabled]);

  useEffect(() => {
    const audioTrack = localAudioTrack as IMicrophoneAudioTrack & {
      processor: unknown;
    };
    if (audioEnabled && localAudioTrack && !audioTrack.processor) {
      // oxlint-disable-next-line typescript/ban-ts-comment
      // @ts-ignore
      const audioProcessor = denoiser.createProcessor();

      localAudioTrack
        ?.pipe(audioProcessor)
        .pipe(localAudioTrack.processorDestination);
      audioProcessor.setMode(AIDenoiserProcessorMode.NSNG);
      audioProcessor.setLevel(AIDenoiserProcessorLevel.SOFT);
      audioProcessor.enable();
    }
  }, [audioEnabled, localAudioTrack]);

  useEffect(() => {
    remoteUsers.forEach((user) => {
      client.setStreamFallbackOption(user.uid, 2);
    });
  }, [remoteUsers, client]);

  useEffect(() => {
    const isVideoPermissionError = localVideoTrackError?.message
      .toLocaleLowerCase()
      .includes("permission_denied");
    if (isVideoPermissionError) {
      toast(
        t({
          id: "call-conference.video-no-browser-permission",
          defaultMessage:
            "Unable to access the camera. Please check your browser permissions and ensure that access to the camera is allowed.",
        }),
        {
          variant: "error",
        }
      );
      setVideoEnabled(false);
    }
  }, [localVideoTrackError, t, toast]);
  useEffect(() => {
    const isAudioPermissionError = localAudioTrackError?.message
      .toLocaleLowerCase()
      .includes("permission_denied");

    if (isAudioPermissionError) {
      toast(
        t({
          id: "call-conference.audio-no-browser-permissions",
          defaultMessage:
            "Unable to access the microphone. Please check your browser permissions and ensure that access to the microphone is allowed.",
        }),
        {
          variant: "error",
        }
      );
      setAudioEnabled(false);
    }
  }, [localAudioTrackError, t, toast]);
  const toggleAudio = useCallback(
    async (shouldSaveSetting = true) => {
      const nextIsDisabled = !localAudioTrack?.enabled;

      if (!localAudioTrack) {
        setAudioEnabled(true);
      } else {
        await localAudioTrack?.setEnabled(nextIsDisabled);
        setAudioEnabled(nextIsDisabled);
      }
      if (shouldSaveSetting) {
        setCallsSettings({
          isAudioOn: nextIsDisabled,
        });
      }
    },
    [localAudioTrack, setCallsSettings]
  );

  const toggleProcessor = useCallback(async () => {
    // oxlint-disable-next-line typescript/ban-ts-comment
    // @ts-ignore
    if (videoProcessor?.current.enabled) {
      // oxlint-disable-next-line typescript/ban-ts-comment
      // @ts-ignore
      videoProcessor.current.release();
    } else {
      // oxlint-disable-next-line typescript/ban-ts-comment
      // @ts-ignore
      videoProcessor?.current?.enable();
    }
    setProcessorEnabled(!processorEnabled);
  }, [processorEnabled]);
  const leaveCall = useCallback(async () => {
    if (audioEnabled) {
      toggleAudio(false);
    }
    if (videoEnabled) {
      toggleVideo(false);
    }
    await client.leave();
  }, [audioEnabled, client, toggleAudio, toggleVideo, videoEnabled]);

  const params: ActiveCallParams | null = useMemo(
    () =>
      rtcProps
        ? {
            localAudioTrack,
            localVideoTrack,
            remoteUsers,
            toggleVideo,
            toggleAudio,
            toggleProcessor,
            leaveCall,
            videoEnabled,
            audioEnabled,
            videoProcessor: videoProcessor.current,
          }
        : null,
    [
      localAudioTrack,
      localVideoTrack,
      remoteUsers,
      rtcProps,
      toggleVideo,
      toggleAudio,
      toggleProcessor,
      leaveCall,
      videoEnabled,
      audioEnabled,
    ]
  );

  return params;
};
